<script setup lang="ts">
import type { IRateResponse, IRateTooltipParam } from '~/components/widget/rates'
import type { ToolboxComponentOption } from 'echarts'
import { CHART_DATE_RANGES } from '~/lib/constants'
import { LazyVChart } from '~/components/ui/chart'

const props = defineProps<{
  title: string
}>()
const { t } = useI18n()
const config = useRuntimeConfig()
const currencies = ['usd', 'eur', 'rub', 'kzt'] as const
type Currency = (typeof currencies)[number]

const selectedCurrency = ref<Currency>('usd')
const selectedRange = ref('lastMonth')

const customFrom = ref<string>('')
const customTo = ref<string>('')

const ranges = CHART_DATE_RANGES

const selectRange = (key: string) => {
  selectedRange.value = key
  customFrom.value = ''
  customTo.value = ''
}

watch([customFrom, customTo], ([from, to]) => {
  if (from && to && new Date(from) < new Date(to)) {
    selectedRange.value = 'custom'
  }
})

const url = computed(() => {
  return `${config.public.baseApiUrl}/rates/central/${selectedCurrency.value}?type=regular`
})

const fetchKey = computed(() => `rates-charts-central-${selectedCurrency.value}`)

const { data: rates } = useFetch<IRateResponse>(url, {
  key: fetchKey,
  server: false
})

const xAxisData = computed(
  () => rates.value?.central?.map(([timestamp]) => new Date(timestamp).toISOString()) ?? []
)

const centralSeriesData = computed(() => rates.value?.central?.map(([, value]) => value) ?? [])

const getStartIndexForRange = (range: string, lastDate: Date, xAxisData: string[]) => {
  let startDate
  switch (range) {
    case 'lastMonth':
      startDate = new Date(lastDate)
      startDate.setMonth(lastDate.getMonth() - 1)
      break
    case 'last3Months':
      startDate = new Date(lastDate)
      startDate.setMonth(lastDate.getMonth() - 3)
      break
    case 'last6Months':
      startDate = new Date(lastDate)
      startDate.setMonth(lastDate.getMonth() - 6)
      break
    case 'lastYear':
      startDate = new Date(lastDate.getFullYear() - 1, lastDate.getMonth(), lastDate.getDate())
      break
    case 'fromBeginningOfYear':
      startDate = new Date(lastDate.getFullYear(), 0, 1)
      break
    case 'allTime':
      return 0
    default:
      return 0
  }
  const index = xAxisData.findIndex((date) => new Date(date) >= startDate)
  return index !== -1 ? index : 0
}

const customStartIndex = computed(() => {
  if (!customFrom.value || !customTo.value || !xAxisData.value.length) return 0
  const startDate = new Date(customFrom.value)
  const index = xAxisData.value.findIndex((date) => new Date(date) >= startDate)
  return index !== -1 ? index : 0
})

const customEndIndex = computed(() => {
  if (!customFrom.value || !customTo.value || !xAxisData.value.length)
    return xAxisData.value.length - 1
  const endDate = new Date(customTo.value)
  const index = xAxisData.value.findIndex((date) => new Date(date) > endDate)
  return index !== -1 ? index - 1 : xAxisData.value.length - 1
})

const chartOption = computed(() => {
  if (!xAxisData.value.length) return {}

  const lastDate = new Date(xAxisData.value[xAxisData.value.length - 1])

  const totalPoints = xAxisData.value.length

  const startIndex =
    selectedRange.value === 'custom'
      ? customStartIndex.value
      : getStartIndexForRange(selectedRange.value, lastDate, xAxisData.value)

  const endIndex =
    selectedRange.value === 'custom' ? customEndIndex.value : totalPoints - 1

  const startPercent = (startIndex / (totalPoints - 1)) * 100
  const endPercent = (endIndex / (totalPoints - 1)) * 100

  const visibleDays = endIndex > startIndex
    ? (new Date(xAxisData.value[endIndex]).getTime() - new Date(xAxisData.value[startIndex]).getTime()) / 86400000
    : 0

  const toolbox: ToolboxComponentOption = {
    feature: {
      saveAsImage: {
        show: true,
        title: `${t('common.save_title')} ${selectedCurrency.value.toUpperCase()}`,
        name: `${t('widget.bank_page.rates_chart_nbkr.chart.filename_prefix')}${selectedCurrency.value.toUpperCase()}`,
        pixelRatio: 2,
        type: 'png',
        backgroundColor: '#fff'
      }
    },
    top: -5,
    right: -5,
    iconStyle: {
      borderColor: '#2563eb'
    }
  }

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        label: {
          show: true,
          backgroundColor: '#ffffff',
          color: '#000000',
          borderColor: '#000000',
          borderWidth: 1,
          borderRadius: 4,
          shadowColor: 'rgba(0, 0, 0, 0.3)',
          shadowBlur: 4,
          padding: [4, 4],
          fontSize: 12
        }
      },
      formatter: (params: IRateTooltipParam[]) => {
        const date = new Date(params[0].axisValue)
        const dateStr = new Intl.DateTimeFormat('ru-RU', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).format(date)

        return `
          <div style="margin-bottom:4px;">${dateStr}</div>
          <div style="margin: 4px 0;">
            <span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background:${params[0].color};"></span>
            <strong>${t('widget.bank_page.rates_chart_nbkr.chart.tooltip_average')} ${params[0].data.toFixed(2)}</strong>
          </div>
        `
      }
    },
    legend: {
      data: [t('common.average_rate')],
      bottom: 0,
      orient: 'horizontal'
    },
    grid: {
      top: '10px',
      left: '30px',
      right: '30px',
      bottom: '70px',
      containLabel: true
    },
    xAxis: [
      {
        type: 'category',
        boundaryGap: false,
        axisLabel: {
          formatter: (value: string) => {
            const date = new Date(value)
            if (visibleDays <= 90) {
              return date.toLocaleString('ru-RU', { day: 'numeric', month: 'short' })
            }
            return date.toLocaleString('ru-RU', { month: 'short', year: '2-digit' })
          }
        },
        data: xAxisData.value
      }
    ],
    yAxis: {
      type: 'value',
      position: 'right',
      scale: true,
      splitNumber: 10,
      axisLabel: {
        formatter: (value: number) => value.toFixed(2)
      },
      name: t('common.price_in_kgs'),
      nameLocation: 'middle',
      nameRotate: -90,
      nameGap: 50,
      nameTextStyle: { fontSize: 12 },
      min: ({ min }: { min: number }) => min - 0.1,
      max: ({ max }: { max: number }) => max + 0.1
    },
    series: [
      {
        name: t('common.average_rate'),
        type: 'line',
        smooth: true,
        data: centralSeriesData.value,
        color: '#2563eb',
        lineStyle: { width: 2 }
      }
    ],
    dataZoom: [
      {
        type: 'slider',
        xAxisIndex: [0],
        start: startPercent,
        end: endPercent,
        bottom: 40,
        brushSelect: false,
        minSpan: 1,
        showDetail: false
      }
    ],
    toolbox
  }
})
</script>

<template>
  <div>
    <div class="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-0">
      <Heading tag="h2" class="h4"
        >{{ $t('widget.bank_page.rates_chart_nbkr.heading') }} {{ props.title }}</Heading
      >
      <div class="flex flex-col gap-4 md:flex-row">
        <div class="flex gap-2">
          <Button
            v-for="currency in currencies"
            :key="currency"
            :variant="selectedCurrency === currency ? 'blue' : 'link'"
            size="sm"
            @click="selectedCurrency = currency"
          >
            {{ currency.toUpperCase() }}
          </Button>
        </div>
      </div>
    </div>

    <div class="flex flex-col justify-between gap-2 md:flex-row md:gap-0">
      <div class="flex flex-wrap items-center gap-2">
        <Button
          v-for="range in ranges"
          :key="range.key"
          size="sm"
          :variant="selectedRange === range.key ? 'blue' : 'link'"
          @click="selectRange(range.key)"
        >
          {{ range.label }}
        </Button>
      </div>
      <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div class="flex items-center gap-2">
          <span>{{ $t('common.from') }}</span>
          <Input
            v-model="customFrom"
            type="date"
            :max="customTo || undefined"
            class="max-w-[140px]"
            :aria-label="$t('common.from')"
          />
        </div>
        <div class="flex items-center gap-2">
          <span>{{ $t('common.to') }}</span>
          <Input
            v-model="customTo"
            type="date"
            :min="customFrom || undefined"
            class="max-w-[140px]"
            :aria-label="$t('common.to')"
          />
        </div>
      </div>
    </div>

    <div class="relative mt-4 h-[400px] w-full">
      <LazyVChart :option="chartOption" :update-options="{ notMerge: true }" autoresize class="h-full w-full" />
    </div>
  </div>
</template>
